import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  identificacao as api,
  type EntregaNoLog,
  type FiltroDoLog,
  type Matriz,
  type NivelDeIdentificacao,
} from '../api/identificacao'
import { ErroApi } from '../api/client'
import { Botao } from '../componentes/Botao'
import { Campo } from '../componentes/Campo'
import { Estado } from '../componentes/Estado'
import { formatar } from '../i18n'
import { marcaDaRota } from './marcaDaRota'
import './usuarios.css'

/**
 * O log de entrega de documento e a matriz que o explica (demanda 027 · `P-29`).
 *
 * 🔴 **Responde a pergunta que só aparece quando algo deu errado:** *"quem
 * recebeu aquele boleto?"* — feita por quem não recebeu, ou sobre quem recebeu o
 * que não devia.
 *
 * A matriz fica junto, recolhida, porque ela é a **legenda** da coluna "nível":
 * sem ela, "ConfirmacaoDeDado" numa linha do log não diz nada a quem consulta.
 */
export function LogDeEntrega() {
  const { t } = useTranslation()
  const marca = marcaDaRota()

  const [matriz, setMatriz] = useState<Matriz | null>(null)
  const [linhas, setLinhas] = useState<EntregaNoLog[] | null>(null)
  const [total, setTotal] = useState(0)
  const [falhou, setFalhou] = useState(false)
  const [semPermissao, setSemPermissao] = useState(false)

  const [filtro, setFiltro] = useState<FiltroDoLog>({})
  const [rascunho, setRascunho] = useState<FiltroDoLog>({})

  const carregar = useCallback(async () => {
    if (!marca) return

    setFalhou(false)
    setSemPermissao(false)

    try {
      const [m, pagina] = await Promise.all([api.matriz(), api.log(marca, filtro)])

      setMatriz(m)
      setLinhas(pagina.linhas)
      setTotal(pagina.total)
    } catch (e) {
      // 403 é diferente de erro: quem não tem `AuditoriaVer` precisa ler "você
      // não tem acesso", não "algo deu errado" — que faria pedir suporte.
      if (e instanceof ErroApi && e.status === 403) setSemPermissao(true)
      else setFalhou(true)
    }
  }, [marca, filtro])

  useEffect(() => {
    void carregar()
  }, [carregar])

  if (!marca) return <Estado tipo="erro" descricao={t('logDeEntrega.semMarca')} />

  if (semPermissao) return <Estado tipo="semPermissao" />

  if (falhou)
    return <Estado tipo="erro" acao={<Botao onClick={carregar}>{t('comum.tentarDeNovo')}</Botao>} />

  if (!linhas || !matriz) return <Estado tipo="carregando" />

  const filtrando = Object.values(filtro).some(Boolean)

  return (
    <main className="usuarios">
      <header className="usuarios__cabecalho">
        <div>
          <h1 className="usuarios__titulo">{t('logDeEntrega.titulo')}</h1>
          <p className="usuarios__descricao">{t('logDeEntrega.descricao', { total })}</p>
        </div>
      </header>

      <MatrizRecolhida matriz={matriz} />

      <form
        className="usuarios__filtros"
        onSubmit={(e) => {
          e.preventDefault()
          setFiltro(rascunho)
        }}
      >
        {/* 🔴 `<select>` fica em `<label>` cru, e NÃO dentro de `<Campo>`.
            `Campo` renderiza um `<input>` e ignora `children` — na 052 isso fez
            um `<select>` sumir da tela sem erro nenhum. */}
        <label className="usuarios__filtro">
          <span>{t('logDeEntrega.tipo')}</span>
          <select
            value={rascunho.tipo ?? ''}
            onChange={(e) => setRascunho({ ...rascunho, tipo: e.target.value || undefined })}
          >
            <option value="">{t('logDeEntrega.todosOsTipos')}</option>
            {matriz.linhas.map((l) => (
              <option key={l.tipo} value={l.tipo}>
                {t(`logDeEntrega.documento.${l.tipo}`, { defaultValue: l.tipo })}
              </option>
            ))}
          </select>
        </label>

        <Campo
          rotulo={t('logDeEntrega.referencia')}
          value={rascunho.referencia ?? ''}
          onChange={(e) => setRascunho({ ...rascunho, referencia: e.target.value || undefined })}
        />

        <Campo
          rotulo={t('logDeEntrega.de')}
          type="date"
          value={rascunho.de?.slice(0, 10) ?? ''}
          onChange={(e) =>
            setRascunho({ ...rascunho, de: e.target.value ? `${e.target.value}T00:00:00Z` : undefined })
          }
        />

        <Campo
          rotulo={t('logDeEntrega.ate')}
          type="date"
          value={rascunho.ate?.slice(0, 10) ?? ''}
          onChange={(e) =>
            setRascunho({ ...rascunho, ate: e.target.value ? `${e.target.value}T23:59:59Z` : undefined })
          }
        />

        <Botao type="submit" variante="primario">
          {t('logDeEntrega.filtrar')}
        </Botao>

        {filtrando && (
          <Botao
            variante="secundario"
            onClick={() => {
              setRascunho({})
              setFiltro({})
            }}
          >
            {t('logDeEntrega.limpar')}
          </Botao>
        )}
      </form>

      {linhas.length === 0 ? (
        <Estado
          tipo="vazio"
          descricao={filtrando ? t('logDeEntrega.vazioFiltrado') : t('logDeEntrega.vazio')}
          acao={
            filtrando ? (
              <Botao
                onClick={() => {
                  setRascunho({})
                  setFiltro({})
                }}
              >
                {t('logDeEntrega.limpar')}
              </Botao>
            ) : undefined
          }
        />
      ) : (
        <div className="usuarios__tabela-rolagem">
          <table className="usuarios__tabela">
            <thead>
              <tr>
                <th>{t('logDeEntrega.quando')}</th>
                <th>{t('logDeEntrega.documento_')}</th>
                <th>{t('logDeEntrega.referencia')}</th>
                <th>{t('logDeEntrega.paraQuem')}</th>
                <th>{t('logDeEntrega.nivel')}</th>
                <th>{t('logDeEntrega.confirmacao')}</th>
                <th>{t('logDeEntrega.assistente')}</th>
              </tr>
            </thead>

            <tbody>
              {linhas.map((l) => (
                <tr key={l.id}>
                  <td>{formatar.dataHora(l.entregueEm)}</td>
                  <td>{t(`logDeEntrega.documento.${l.tipo}`, { defaultValue: l.tipo })}</td>
                  <td>{l.referencia}</td>
                  <td>
                    {l.destinatario}
                    {l.contato && <span className="usuarios__secundario"> · {l.contato}</span>}
                  </td>
                  <td>{rotuloDoNivel(t, l.nivelAtingido)}</td>

                  {/* 🔴 O que aparece é o NOME do campo. O valor não existe no
                      banco, então não existe tela que possa mostrá-lo — que é a
                      forma mais confiável de garantir que ninguém o mostre. */}
                  <td>
                    {l.campoConfirmado
                      ? t(`logDeEntrega.campo.${l.campoConfirmado}`, {
                          defaultValue: l.campoConfirmado,
                        })
                      : '—'}
                  </td>

                  <td>{l.assistente ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  )
}

/**
 * A matriz, recolhida e **somente leitura**.
 *
 * 🔴 A tela diz por que não há botão de editar. Deixar a explicação de fora
 * geraria a pergunta "cadê?", e a resposta mais provável seria alguém abrindo
 * uma rota de edição para responder.
 */
function MatrizRecolhida({ matriz }: { matriz: Matriz }) {
  const { t } = useTranslation()

  return (
    <details className="usuarios__bloco">
      <summary>{t('logDeEntrega.matriz.titulo')}</summary>

      <p className="usuarios__descricao">
        {t('logDeEntrega.matriz.descricao', {
          minutos: matriz.validadeMinutos,
          tentativas: matriz.tentativasMaximas,
          digitos: matriz.digitosDeConfirmacao,
        })}
      </p>

      {!matriz.editavel && (
        <p className="usuarios__descricao">{t('logDeEntrega.matriz.somenteLeitura')}</p>
      )}

      <table className="usuarios__tabela">
        <thead>
          <tr>
            <th>{t('logDeEntrega.documento_')}</th>
            <th>{t('logDeEntrega.nivelExigido')}</th>
            <th>{t('logDeEntrega.matriz.origem')}</th>
          </tr>
        </thead>

        <tbody>
          {matriz.linhas.map((l) => (
            <tr key={l.tipo}>
              <td>{t(`logDeEntrega.documento.${l.tipo}`, { defaultValue: l.tipo })}</td>
              <td>{rotuloDoNivel(t, l.nivel)}</td>
              <td>
                {l.deParametro
                  ? t('logDeEntrega.matriz.deParametro')
                  : t('logDeEntrega.matriz.doPadrao')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  )
}

function rotuloDoNivel(
  t: (chave: string, opcoes?: Record<string, unknown>) => string,
  nivel: NivelDeIdentificacao,
) {
  return t(`logDeEntrega.nivelNome.${nivel}`, { defaultValue: nivel })
}
